/* eslint-disable no-continue */
// @ts-strict-ignore
import React from 'react';
import type {
  Action,
  AggregationComponent,
  Attribute,
  PopulatedThreshold,
  ReasonCodeGroup,
  ServicesByName,
  ThresholdRight,
  WorkflowInfo,
} from 'flow/store/engine/types';
import type { DropdownOption, IsolatedEditingBlockOrigin } from 'flow/editor/components/NodeEditorDrawer/types';
import { getAllowedAttributesWithMatchingDataType, isActionOfType } from 'flow/service/actions';
import { ActionType, AggregationType } from 'flow/store/engine/constants';
import { Icon, IconName } from '@usealloy/component-library';
import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';
import {
  NUMERIC_VALUE_MIN,
  NUMERIC_VALUE_MAX,
  listDelineators,
  noReasonCodeGroupsOption,
} from './constants';
import { BEST_EFFORT_PARSING_DEFAULT_STRING, minMaxLimitRequiredActionTypes } from 'flow/service/thresholds';
import {
  buildActionOptions,
  isMatchingRegexOperator,
  isNumericDataType,
  isMultiSelectRHS,
  isListInclusionOperator,
  truncateLabel,
  canDynamicallyCompareActions,
} from '../helpers';
import { ThresholdRightAttributeOrigin } from 'flow/editor/components/NodeEditorDrawer/types';
import type { NodeLineage } from 'flow/service/graph';
import type { RHSComponentType } from './types';
import { rhsComponentType } from './types';
import { DEFAULT_DOLLAR_MAX, DEFAULT_INTEGER_MAX, DEFAULT_INTEGER_MIN } from 'flow/editor/components/thresholds/constants';
import { isAggregationServiceAction } from 'flow/service/services/services';
import { isValidRegex } from 'flow/editor/components/MenuBar/MenuBarModals/ValidateAndSaveVersionModals/validateRegex';

export function buildDateTimeDropdownOption({
  lhsDataType,
  populatedThreshold,
  lengthLimit,
}: {
  lhsDataType: string;
  populatedThreshold: PopulatedThreshold;
  lengthLimit: number;
}): DropdownOption {
  const rightAttributeValueString = getRightAttributeValueAsString(
    populatedThreshold.right.attributeValue,
  );

  const formattedDateTime = formatDateTime({
    value: rightAttributeValueString,
    lhsDataType,
  });

  return {
    icon: <Icon icon={IconName.Calendar} />,
    label: `Edit ${capitalize(lhsDataType)}`,
    value: formattedDateTime,
    meta: {
      hideIcon: true,
      shortenedLabel: truncateLabel(formattedDateTime, lengthLimit),
    },
  };
}

/**
 * Builds dropdown options for dynamic RHS (right-hand side) values in threshold rules.
 * Filters available actions to only those that can be meaningfully compared with the LHS action.
 */
export function buildDynamicRHSOptions({
  actions,
  lengthLimit,
  lhsDataType,
  nodeLineage,
  populatedThreshold,
  services,
  sourceAction,
  workflow,
}: {
  actions: Record<string, Action>;
  lengthLimit: number;
  lhsDataType: string;
  nodeLineage: NodeLineage;
  populatedThreshold: PopulatedThreshold;
  services: ServicesByName;
  sourceAction: Action;
  workflow: WorkflowInfo;
}): DropdownOption[] {
  const actionOptions = buildActionOptions({
    actions,
    sourceAction,
    services,
    nodeLineage,
    lengthLimit,
    thresholdSide: populatedThreshold.right,
  }).filter(({ value }) => {
    const action = actions[value];

    // Exclude matrix models from RHS options
    if (isActionOfType(action, [
      ActionType.MATRIX_MODEL, ActionType.IMPORTED_MATRIX_MODEL,
    ])) {
      return false;
    }

    // For service actions, ensure they have attributes matching the LHS data type
    if (isActionOfType(action, ActionType.SERVICE)) {
      if (!serviceActionHasMatchingAttributes(action, services, lhsDataType, workflow.type)) {
        return false;
      }
    }

    // Only include actions that can be dynamically compared with the LHS action
    return canDynamicallyCompareActions(populatedThreshold.left.action, action);
  });

  return actionOptions;
}

export function buildStaticRHSOptions({
  lengthLimit,
  lhsDataType,
  populatedThreshold,
  reasonCodeGroups,
}: {
  lengthLimit: number;
  lhsDataType: string;
  populatedThreshold: PopulatedThreshold;
  reasonCodeGroups: ReasonCodeGroup[];
}): DropdownOption[] {
  const {
    right: { attributeValue: rightAttributeValue },
    left: { action: leftAction, attribute: leftAttribute },
  } = populatedThreshold;

  const rightAttributeValueString = getRightAttributeValueAsString(rightAttributeValue);

  if (isActionOfType(leftAction, ActionType.TAG)
      || isActionOfType(leftAction, ActionType.OUTCOME_REASON)) {
    return [
      { label: 'is set', value: 'true' },
      { label: 'is not set', value: 'false' }];
  }

  switch (lhsDataType) {
    case 'boolean': {
      return [
        { label: 'is true', value: 'true' },
        { label: 'is false', value: 'false' },
      ];
    }
    case 'decimal':
    case 'integer': {
      return rightAttributeValue === null
        ? [{ label: '', value: '' }]
        : [{
          value: rightAttributeValueString,
          label: rightAttributeValueString,
          meta: {
            shortenedLabel: truncateLabel(rightAttributeValueString, lengthLimit),
          },
        }];
    }
    case 'string': {
      if (isMultiSelectRHS({ lhsDataType, leftAttribute })) {
        const leftAttributeValues = leftAttribute.values ? leftAttribute.values.split(',') : [];
        return leftAttributeValues.map(value => ({ label: value, value }));
      }
      return rightAttributeValue === null
        ? [{ label: '', value: '' }]
        : [{
          value: rightAttributeValueString,
          label: rightAttributeValueString,
          meta: {
            shortenedLabel: truncateLabel(rightAttributeValueString, lengthLimit),
          },
        }];
    }
    case 'reasoncodes': {
      const reasonCodeItems = [...reasonCodeGroups]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ name, id }) => ({ label: name, value: String(id) }));

      if (reasonCodeItems.length === 0) {
        return [noReasonCodeGroupsOption];
      }
      return reasonCodeItems;
    }
    case 'matching': {
      return [
        { label: 'true', value: 'true' },
        { label: 'false', value: 'false' },
      ];
    }
    default: return [];
  }
}

export function serializeRHSMultiSelectValue({
  isDynamic,
  lhsDataType,
  populatedThreshold,
}: {
  isDynamic: boolean;
  lhsDataType: string;
  populatedThreshold: PopulatedThreshold;
}): string | string[] {
  const {
    right: {
      attributeValue,
      reasonCodeGroupId,
      leftTagActionResult,
      action: rightAction,
    },
    left: { action: leftAction, attribute: leftAttribute },
  } = populatedThreshold;

  if (isDynamic) {
    return rightAction.publicId;
  }

  if (isActionOfType(leftAction, ActionType.TAG)) {
    return String(leftTagActionResult);
  }

  if (isActionOfType(leftAction, ActionType.OUTCOME_REASON)) {
    return getRightAttributeValueAsString(attributeValue);
  }

  if (lhsDataType === 'reasoncodes') {
    return String(reasonCodeGroupId);
  }

  if (lhsDataType === 'date' || lhsDataType === 'timestamp') {
    return formatDateTime({ value: String(attributeValue), lhsDataType });
  }

  if (isMultiSelectRHS({ lhsDataType, leftAttribute })) {
    return attributeValue ? String(attributeValue).split(',') : [];
  }

  return getRightAttributeValueAsString(attributeValue);
}

/**
 * Handles both multi-select (array) and single-value RHS.
 * Returns the matching RHS option.
 */
export function getSelectedRHSOption({
  options,
  value,
}: {
  options: DropdownOption[];
  value: string | string[];
}): DropdownOption | DropdownOption[] | undefined {
  if (Array.isArray(value)) {
    return options.filter(option => value.includes(option.value));
  }
  return options.find(option => option.value === value);
}

export function filterSelectableRHSOptions({
  options,
  lhsDataType,
  indexedReasonCodeGroups,
}: {
  options: DropdownOption[];
  lhsDataType: string;
  indexedReasonCodeGroups: Record<string, ReasonCodeGroup>;
}): DropdownOption[] {
  if (lhsDataType === 'reasoncodes') {
    return options.filter(option => (
      option.value === noReasonCodeGroupsOption.value
      || !indexedReasonCodeGroups[option.value].archived
    ));
  }
  return options;
}

export function getMinMaxValueCap({
  lhsDataType,
  leftAction,
  leftAttribute,
  aggregationComponents,
}: {
  lhsDataType: string;
  leftAction: Action;
  leftAttribute?: Attribute;
  aggregationComponents?: AggregationComponent[];
}): {
    min: number;
    max: number;
  } | null {
  const numericCap = {
    min: NUMERIC_VALUE_MIN,
    max: NUMERIC_VALUE_MAX,
  };

  if (!leftAttribute || lhsDataType === 'string') {
    return null;
  }

  if (isAggregationServiceAction(leftAction) && aggregationComponents?.length) {
    const { aggregationType } = aggregationComponents[0].aggregation;
    if (aggregationType === AggregationType.COUNT
      || aggregationType === AggregationType.COUNT_DISTINCT) {
      return { min: 0, max: DEFAULT_DOLLAR_MAX };
    }
    return { min: NUMERIC_VALUE_MIN, max: DEFAULT_DOLLAR_MAX };
  }

  if (minMaxLimitRequiredActionTypes.has(leftAction.type)) {
    if (leftAction.type === ActionType.MATRIX_MODEL) {
      const { matrixDataType } = leftAction.matrixModel;
      return matrixDataType === 'string' ? null : numericCap;
    }
    if (['decimal', 'integer'].includes(lhsDataType)) {
      return numericCap;
    }
  }

  const leftAttributeMinMaxArray = leftAttribute?.values ? leftAttribute.values.split(',') : [];
  if (leftAttributeMinMaxArray.length === 2) {
    return {
      min: Number(leftAttributeMinMaxArray[0]),
      max: Number(leftAttributeMinMaxArray[1]),
    };
  }

  return {
    min: DEFAULT_INTEGER_MIN,
    max: DEFAULT_INTEGER_MAX,
  };
}

export function getMinMaxValueCapTooltipContent({
  lhsDataType,
  minMaxValueCap,
}: {
  lhsDataType: string;
  minMaxValueCap: { min: number; max: number };
}): string {
  const isCapDefaultIntegerValue = minMaxValueCap.min === DEFAULT_INTEGER_MIN
  && minMaxValueCap.max === DEFAULT_INTEGER_MAX;
  const defaultPromptForValueCap = `Enter ${lhsDataType === 'decimal' ? 'a ' : 'an '}${lhsDataType}`;
  return isCapDefaultIntegerValue
    ? defaultPromptForValueCap
    : `${defaultPromptForValueCap} between ${minMaxValueCap.min} and ${minMaxValueCap.max}`;
}

export function getMultiSelectRHSValue({
  selected,
  supportsBestEffortParsing,
}: {
  selected: DropdownOption[];
  supportsBestEffortParsing: boolean;
}): string | null {
  const stringValue = selected.map(option => option.value).join(',');
  return supportsBestEffortParsing && !stringValue ? null : stringValue;
}

export function getRightAttributeValue({
  lhsDataType,
  shouldUseDefaultString,
  value,
}: {
  lhsDataType: string;
  shouldUseDefaultString: boolean;
  value: string;
}): string {
  if (isNumericDataType(lhsDataType)) {
    let numericValue = Number.parseFloat(value);

    if (isNaN(numericValue)) {
      numericValue = 0;
    }

    if (lhsDataType === 'integer') {
      numericValue = Math.round(numericValue);
    }

    return numericValue.toString();
  }

  if (shouldUseDefaultString && value.trim() === '') {
    return BEST_EFFORT_PARSING_DEFAULT_STRING;
  }

  return value;
}

export function clampNumericValueToMinMax({
  value,
  minMaxValueCap,
}: {
  value: number;
  minMaxValueCap: { min: number; max: number };
}): {
    clampedValue: number;
    wasChanged: boolean;
    changeType?: 'minimum' | 'maximum';
  } {
  const { min, max } = minMaxValueCap;

  if (value < min) {
    return { clampedValue: min, wasChanged: true, changeType: 'minimum' };
  }
  if (value > max) {
    return { clampedValue: max, wasChanged: true, changeType: 'maximum' };
  }

  return { clampedValue: value, wasChanged: false };
}

export function getCustomPlaceholder({
  hasNoReasonCodes,
  isMultiSelect,
  lhsDataType,
  shouldUseDefaultString,
}: {
  hasNoReasonCodes: boolean;
  isMultiSelect: boolean;
  lhsDataType: string;
  shouldUseDefaultString: boolean;
}): string {
  if (lhsDataType === 'reasoncodes' && hasNoReasonCodes) {
    return 'No Reason Code Groups';
  }
  if (shouldUseDefaultString) {
    return isMultiSelect ? 'make_selection' : 'enter_string';
  }
  return '...';
}

export function getEditorOriginType({
  lhsDataType,
  operator,
}: {
  lhsDataType: string;
  operator: string;
}): ThresholdRightAttributeOrigin {
  return isMatchingRegexOperator({ lhsDataType, operator })
    ? ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REGEX
    : ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_LIST_INCLUSION;
}

export function getRightAttributeValueAsString(rightAttributeValue: string | number) {
  return rightAttributeValue === BEST_EFFORT_PARSING_DEFAULT_STRING
    ? '' : String(rightAttributeValue);
}

export const getRHSComponentType = ({
  lhsDataType,
  operator,
  leftAttribute,
  rightAttributeValue,
  actionType,
  isDynamic,
}: {
  lhsDataType: string;
  operator: string;
  leftAttribute: Attribute;
  rightAttributeValue: ThresholdRight['attributeValue'];
  actionType: string;
  isDynamic: boolean;
}): RHSComponentType => {
  if (operator === 'range') {
    return rhsComponentType.range;
  }

  if (isDynamic) {
    return rhsComponentType.multiSelect;
  }

  if (['date', 'timestamp'].includes(lhsDataType)) {
    return rhsComponentType.datePicker;
  }

  const hasCommaDelineatedValues = rightAttributeValue
    && String(rightAttributeValue).split(',').length > 1;

  const isMultiSelect = isMultiSelectRHS({ lhsDataType, leftAttribute });

  // '=' and '!=' supports list inclusion when there is a comma delineated value involved
  const isList = isListInclusionOperator(operator)
    || (['e', 'ne'].includes(operator)
    && hasCommaDelineatedValues
    && !isMultiSelect);

  if (isMatchingRegexOperator({ lhsDataType, operator }) || isList) {
    return rhsComponentType.isolatedEditor;
  }

  const isSingleStringField = lhsDataType === 'string' && !isMultiSelect;
  const isNotOutcomeReason = actionType !== ActionType.OUTCOME_REASON;

  const isCreateable = (isSingleStringField || isNumericDataType(lhsDataType))
    && isNotOutcomeReason;

  if (isCreateable) {
    return rhsComponentType.creatable;
  }

  if (lhsDataType === 'matching') {
    return operator === 'e' ? rhsComponentType.multiSelect : rhsComponentType.creatable;
  }

  return rhsComponentType.multiSelect;
};

export const formatRawValueToDollars = (rawValue: string): string => {
  const amount = parseFloat(rawValue);
  return `$${(amount / 100).toFixed(2)}`;
};

export const convertDollarValueToRaw = (dollarValue: string): string => {
  let amount = parseFloat(dollarValue.replace(/\$/g, ''));
  amount = parseFloat(amount.toFixed(2));
  return Math.round(amount * 100).toString(); // MLA dollar integer value should be formatted like this
};

function formatDateTime({
  value,
  lhsDataType,
}: {
  value: string;
  lhsDataType: string;
}) {
  if (!value) return '';
  if (lhsDataType === 'timestamp') {
    return dayjs.utc(value).format('MMM DD, YYYY h:mm A');
  }
  return dayjs(value).format('MMM DD, YYYY');
}

function serviceActionHasMatchingAttributes(
  action: Action,
  services: ServicesByName,
  lhsDataType: string,
  workflowType: string,
): boolean {
  const service = services[action.service.internalName];
  if (!service) return false;

  const availableAttributesOfAction = getAllowedAttributesWithMatchingDataType({
    service,
    incomingDataType: lhsDataType,
    workflowType,
  });

  return Object.values(availableAttributesOfAction).length > 0;
}

export const isValidIntegerList = (str: string) => {
  for (const char of str) {
    if (listDelineators.has(char)) continue;
    if (isNaN(parseFloat(char))) return false;
  }
  return true;
};

export const isValidDecimalList = (str: string) => {
  for (const char of str) {
    if (listDelineators.has(char)) continue;
    if (char === '.') continue;
    if (isNaN(parseFloat(char))) return false;
  }
  return true;
};

export const selectIsolatedEditorValidator = (
  mode: IsolatedEditingBlockOrigin, dataType: string,
) => {
  if (mode === ThresholdRightAttributeOrigin.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REGEX) {
    return isValidRegex;
  }
  // only other possible options at the moment are list inclusion
  if (dataType === 'decimal') return isValidDecimalList;

  return isValidIntegerList;
};
